/**
 * Orden de dibujo de un tramo que pasó por varias carreteras.
 *
 * Los trozos (`RouteSegment.roadStretches`) vienen en el orden en que se
 * recorrieron, pero el trazado de cada uno llega SIEMPRE en PK ascendente —
 * tanto de `geoPositionRoads` (el servicio ordena de menor a mayor PK sea cual
 * sea el sentido pedido) como de la malla local. Un trozo que se recorrió
 * bajando hay que darle la vuelta.
 *
 * 🔴 Invertir la lista ENTERA al final no vale, y es lo que se hacía: con un
 * solo trozo da igual, pero con dos deja los trozos al revés ENTRE SÍ y la
 * línea salta de la punta de uno a la punta del otro.
 *
 * Cazado el 2026-08-24 con un recorrido real: bajar de la C-25z (237+100) a la
 * C-25 (82+800) pintaba la vía hasta Cervera y después un salto recto de
 * Cervera a Girona; sobre la curva del Eix Transversal por Vic eso dibujaba un
 * lazo cerrado en mitad del mapa.
 */

import { pkDirection } from './pkCompare';

export interface StretchRange {
  pkInitKm: number;
  pkInitMeter: number;
  pkEndKm: number;
  pkEndMeter: number;
}

/**
 * ¿Este trozo se recorrió bajando de PK?
 *
 * 🔴 Con `pkDirection`, no restando el total en metros: con un kilómetro largo
 * por medio el total dice que `84+1800 → 85+000` BAJA, y sube. Un trozo marcado
 * del revés se dibuja del revés, con las flechas apuntando al lado contrario.
 */
export const isDescendingRange = (rango: StretchRange): boolean =>
  pkDirection(
    { km: rango.pkInitKm, meter: rango.pkInitMeter },
    { km: rango.pkEndKm, meter: rango.pkEndMeter }
  ) < 0;

/**
 * Encadena el trazado de cada trozo en el orden en que se recorrió.
 *
 * `rangos[i]` describe el trozo del que salió `paths[i]`; el trazado llega
 * siempre de PK bajo a PK alto y aquí se invierte el que haga falta. El orden
 * del array NO se toca: ya es el del recorrido.
 */
export function orderStretchPaths<T>(
  rangos: StretchRange[],
  paths: T[][]
): T[] {
  return paths.flatMap((path, i) => {
    const rango = rangos[i];
    // Sin rango que lo describa no hay nada que decidir: se deja como viene.
    if (!rango) return path;
    return isDescendingRange(rango) ? [...path].reverse() : path;
  });
}

export default orderStretchPaths;
